import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { X, ArrowUpRight, Sparkles } from 'lucide-react';

/**
 * RemodDash cross-promo modal.
 *
 * Per user direction (Feb 2026): treat this as helpful, not intrusive.
 *   - Shows ONCE after login/signup.
 *   - Dismissed → snoozed for 30 days via localStorage.
 *   - "Learn more" → snoozed permanently for this browser (until manual reset).
 *   - Permanent "Business Tools · RemodDash" link stays in the AppShell so users
 *     can always find it later.
 *   - Users with `is_admin=true` never see this (they ARE the RMD team).
 *
 * The copy stays neutral: it acknowledges Zita finds opportunities, RemodDash
 * manages them. No hard sell.
 */

const STORAGE_KEY = 'zita.remoddash_promo_snoozed_until';
const SNOOZE_DAYS = 30;
const LEARN_MORE_URL = 'https://remoddash.com';

export const isRemoddashPromoSnoozed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
};

const snooze = (days = SNOOZE_DAYS) => {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch {
    /* localStorage disabled — do nothing; the promo just re-shows next login */
  }
};

export const clearRemoddashSnooze = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
};

export const RemoddashPromo = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Super Admin never sees the promo — they ARE the RMD team.
    if (user.is_admin) return;
    // Only show for users with active access (unpaid trials should focus on
    // completing checkout first, not looking at RMD).
    if (!user.has_active_access) return;
    if (isRemoddashPromoSnoozed()) return;
    // Delay slightly so the user sees the app first.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const closeWith30DaySnooze = () => {
    snooze(SNOOZE_DAYS);
    setOpen(false);
  };

  const openRemoddash = () => {
    // Learn more → snooze effectively permanently (100 years) so they aren't
    // pestered after they've engaged.
    snooze(365 * 100);
    setOpen(false);
    window.open(LEARN_MORE_URL, '_blank', 'noopener,noreferrer');
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Meet RemodDash"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="remoddash-promo"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={closeWith30DaySnooze}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-lg zita-premium-card p-7 sm:p-8 zita-fade-up">
        <button
          type="button"
          onClick={closeWith30DaySnooze}
          aria-label="Close"
          data-testid="remoddash-promo-close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-zita-sand/50 text-zita-muted"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--zita-orange-soft))] flex items-center justify-center">
            <Sparkles size={16} className="zita-orange-text" />
          </div>
          <div className="text-xs uppercase tracking-[0.16em] zita-ink-muted">
            Part of the <span className="remod-accent font-semibold">RemodWorks</span> AI Ecosystem
          </div>
        </div>

        <h2 className="font-serif text-3xl zita-ink leading-tight">
          Running a business?
        </h2>
        <p className="mt-3 text-sm zita-ink-soft leading-relaxed">
          Found something worth selling? Need customers, scheduling, estimates,
          invoices, payments, or dispatch?
        </p>
        <p className="mt-3 text-sm zita-ink-soft leading-relaxed">
          <span className="font-semibold zita-ink">RemodDash</span> is our
          complete business operating system built for service companies and
          owner-operators. Zita finds the opportunities — RemodDash helps you
          run the business around them.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={openRemoddash}
            data-testid="remoddash-promo-learn-more"
            className="zita-btn-orange"
          >
            Learn more <ArrowUpRight size={14} />
          </button>
          <button
            type="button"
            onClick={closeWith30DaySnooze}
            data-testid="remoddash-promo-maybe-later"
            className="zita-btn-ghost-navy"
          >
            Maybe later
          </button>
        </div>

        <div className="mt-4 text-[11px] zita-ink-muted">
          We&apos;ll ask again in 30 days. You can always find RemodDash under{' '}
          <Link to="/settings" className="underline hover:zita-ink">
            Settings → Business Tools
          </Link>
          .
        </div>
      </div>
    </div>
  );
};

export default RemoddashPromo;
